import { getApiEntrypoint, logger } from 'utils';
import { fetchUnguarded, refreshFetch, statusCodeHelperResponse } from './axios';
import type { ICardPaymentCharge, IPaymentPayload } from 'pages/Payment/config';
import type { IBankAccount, IChangeBankAccount } from 'interfaces/activeApllication';

export const sendPtp = async (data: any) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/promise_to_pay';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('sendPtp response', response);
  return await statusCodeHelperResponse(response);
};

export const sendPtpAnonymous = async (slug: string, data: any) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = `fo/v1/${slug}/promise_to_pay`;
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('sendPtpAnonymous response', response);
  return await statusCodeHelperResponse(response);
};

export const sendPaymentMethod = async (data: IPaymentPayload) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = 'fo/v1/pay';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('sendPaymentMethod response', response);
  return await statusCodeHelperResponse(response);
};

export const createPaymentCardCharge = async (data: ICardPaymentCharge) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = 'fo/v1/payment/charge';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('createPaymentCardCharge response', response);
  return await statusCodeHelperResponse(response);
};

export const getPaymentStatus = async (data: string, signal?: AbortSignal) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = `fo/v1/charge-status/${data}`;
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'get',
    url: endpoint,
    signal,
  });
  logger('getPaymentStatus response', response);
  return await statusCodeHelperResponse(response);
};

export const getPaymentCards = async () => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/payment';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'get',
    url: endpoint,
  });
  logger('getPaymentCards response', response);
  return await statusCodeHelperResponse(response);
};

export const saveNewCard = async (data: any) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/payment';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('saveNewCard response', response);
  return await statusCodeHelperResponse(response);
};

export const setCardAsDefault = async (data: any) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/payment/default';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'patch',
    url: endpoint,
    data,
  });
  logger('setCardAsDefault response', response);
  return await statusCodeHelperResponse(response);
};

export const editCardDetails = async (cardUuid: string, data: any) => {
  const axios = refreshFetch();
  const endpoint = `fo/v1/payment/${cardUuid}`;
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'patch',
    url: endpoint,
    data,
  });
  logger('changeCardName response', response);
  return await statusCodeHelperResponse(response);
};

export const deleteCard = async (cardUuid: string) => {
  const axios = refreshFetch();
  const endpoint = `fo/v1/payment/${cardUuid}`;
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'delete',
    url: endpoint,
  });
  logger('deleteCard response', response);
  return await statusCodeHelperResponse(response);
};

export const getBankAccounts = async () => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/personal-account/bank-info';

  const response = await axios({
    method: 'get',
    url: endpoint,
  });
  logger('getBankAccounts response', response);
  const statusData = await statusCodeHelperResponse(response);
  return statusData;
};

export const addUserPaymentMethod = async (data: IBankAccount) => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/personal-account/payout-accounts';
  const response = await axios({
    method: 'post',
    url: endpoint,
    data,
  });
  logger('addUserPaymentMethod response', response);

  const statusData = await statusCodeHelperResponse(response);
  return statusData;
};

export const updateBankAccount = async (bankAccountUuid: string, data: IChangeBankAccount) => {
  const axios = refreshFetch();
  const endpoint = `/fo/v1/personal-account/bank-info/${bankAccountUuid}`;
  const response = await axios({
    method: 'patch',
    url: endpoint,
    data,
  });
  logger('updateBankAccount response', response);

  const statusData = await statusCodeHelperResponse(response);
  return statusData;
};

export const setBankAccountAsDefault = async (data: any) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/personal-account/bank-info/default';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'patch',
    url: endpoint,
    data,
  });
  logger('setBankAccountAsDefault response', response);
  return await statusCodeHelperResponse(response);
};

export const deleteBankAccount = async (bankAccountUuid: string) => {
  const axios = refreshFetch();
  const endpoint = `/fo/v1/personal-account/bank-info/${bankAccountUuid}`;
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'delete',
    url: endpoint,
  });
  logger('deleteBankAccount response', response);
  return await statusCodeHelperResponse(response);
};

export const updateAutoProlongationConsent = async (isEnabled: boolean) => {
  const axios = refreshFetch();
  const endpoint = `/fo/v1/extension/auto-prolongation`;
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'patch',
    url: endpoint,
    data: { isEnabled },
  });
  logger('updateAutoProlongationConsent response', response);
  return await statusCodeHelperResponse(response);
};
